import { Modal } from "@mui/material";
import { cloneDeep, set } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CtaComponent from "../../../components/reusable-ui/cta";
import SwitchComponent from "../../../components/reusable-ui/switch";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { AGGREGATOR_FEATURES_MAP } from "../../../data/outlet-status-constants";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import {
  getOutletLogisticStatus,
  updateOutletLogisticStatus,
} from "../../../redux/slices";
import { allowOnlyNumber, removeSpecialCharacters } from "../../../utils";
import "./style.scss";

interface Props {
  onModalClose: any;
  moreOptionsMenuData: any;
  setApiStateModalData: any;
}

const OutletLogisticStatus = (props: Props) => {
  const { onModalClose, moreOptionsMenuData, setApiStateModalData } = props;
  const { outletData } = moreOptionsMenuData;
  const [data, setData] = useState<any>({
    deliveryStatus: true,
    revertSeconds: "",
    reason: "",
  });
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMsgMap, setErrMsgMap] = useState<any>({});
  const [logisticStatusApiData, setLogisticStatusApiData] = useState<any>({
    message: "",
    showMsg: true,
    data: {},
  });
  const dispatch = useDispatch();

  useEffect(() => {
    let dataToPost = {
      brid: outletData?.brid,
      outletId: outletData?.id,
      type: outletData?.type,
    };
    const callback = (success: boolean, response: any) => {
      if (success) {
        setLogisticStatusApiData({ showMsg: false, data: response });
      } else if (response?.message) {
        setLogisticStatusApiData({ message: response?.message, showMsg: true });
      }
    };
    dispatch(getOutletLogisticStatus({ dataToPost, callback }));
  }, []);

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(data);
    set(dataToUpdate, fieldToUpdate, value);
    setData(dataToUpdate);
    setIsChangesMade(true);
  };

  const changeCountValue = (type: string) => {
    let val: any = parseInt(data?.revertSeconds) || 0;
    if (type === "-") val -= 1;
    else val += 1;

    if (val < 0) val = "";
    handleInputChange(val, "revertSeconds");
  };

  const onSaveCtaClick = () => {
    // if (!data?.reason) {
    //   setErrMsgMap({ reason: "This field is mandatory." });
    //   return;
    // }
    // setErrMsgMap({});
    let dataToPost = {
      brid: outletData?.brid,
      outletId: outletData?.id,
      // type: outletData?.type,
      data,
    };
    const callback = (success: boolean) => {
      if (success) {
        onModalClose();
        setApiStateModalData({
          show: true,
          modalText: `${
            AGGREGATOR_FEATURES_MAP[moreOptionsMenuData?.selectedOptionId].text
          } Updated Successfully.`,
        });
      }
    };
    dispatch(updateOutletLogisticStatus({ dataToPost, callback }));
  };

  function renderSecondsCountBox() {
    return (
      <div className="flex flex-col w-full">
        <p className="mb-8 text-14 text-[#656565]">
          Seconds until current status?
        </p>
        <div className="flex border-[#EBEBEB], border-[1px,] min-h-[44px]">
          <input
            className={`px-16 py-12 text-16 font-normal w-[calc(70%-16px)] mr-[16px]
          border-[#EBEBEB] border rounded-[8px] bg-[#FAFAFA]
        `}
            placeholder="Eg - 1800"
            onChange={(e) =>
              handleInputChange(
                allowOnlyNumber(e.target.value),
                "revertSeconds"
              )
            }
            value={data?.revertSeconds}
          ></input>
          <button
            onClick={() => changeCountValue("-")}
            className={`flex items-center justify-center w-[calc(15%-4px)]
          rounded-[8px] border border-[#EBEBEB] text-28 mr-[4px]`}
          >
            -
          </button>

          <button
            onClick={() => changeCountValue("+")}
            className={`flex items-center justify-center w-[calc(15%-4px)]
          rounded-[8px] border border-[#EBEBEB] text-28 ml-[4px]`}
          >
            +
          </button>
        </div>
      </div>
    );
  }

  function renderJsonErrorMessage(jsonMsg: any) {
    return (
      <div
        className={`json-error-msg max-h-[516px] pb-[16px] px-[24px] mt-[28px]
      overflow-y-auto whitespace-pre-wrap text-14 sm:px-[16px] sm:mt-[12px]
    `}
      >
        {jsonMsg ? JSON.stringify(jsonMsg, null, 2) : null}
      </div>
    );
  }

  function renderModalHeader() {
    return (
      <div
        className={`flex items-center justify-between py-[12px] px-[24px] border-b border-black/[0.15]
          sm:px-[16px]
        `}
      >
        <p className="text-22 font-semibold">
          {`${
            AGGREGATOR_FEATURES_MAP[moreOptionsMenuData?.selectedOptionId].text
          } - ${outletData?.channelDefName}`}
        </p>
        <div
          className="rista-sprite ic-cross-black"
          onClick={onModalClose}
        ></div>
      </div>
    );
  }

  function renderDeliveryStatusInfo() {
    return (
      <div className="px-[24px] py-[10px] text-14 text-[#4C6480] bg-[#F2F8FF] sm:px-[16px]">
        {`Zomato deliveries ${
          logisticStatusApiData?.data?.zomato_logistics_enabled
            ? "enabled"
            : "disabled"
        } and is ${
          logisticStatusApiData?.data?.zomato_logistics_serviceability_status
            ? "active"
            : "inactive"
        }. Self delivery is ${
          logisticStatusApiData?.data?.self_delivery_enabled
            ? "enabled"
            : "disabled"
        }.`}
      </div>
    );
  }

  function renderSelfDeliveryStatusToggleSwitch() {
    return (
      <div className="flex items-center mb-[28px]">
        <p className="text-16 font-medium mr-[10px]">Self-Delivery Status</p>
        <p
          className={`text-14 ${
            !!data?.deliveryStatus ? "text-[#0074FB]" : "text-[#C7C7C7]"
          } font-medium w-[30px] mr-[4px]`}
        >
          {!!data?.deliveryStatus ? "ON" : "OFF"}
        </p>
        <SwitchComponent
          isChecked={!!data?.deliveryStatus}
          inputProps={{ "aria-label": "ant design" }}
          onSwitchClick={(val: any) => handleInputChange(val, "deliveryStatus")}
        />
      </div>
    );
  }

  function renderLogisticStatusUpdateForm() {
    return (
      <>
        <div className="px-[24px] mt-[20px] sm:px-[16px]">
          {renderSelfDeliveryStatusToggleSwitch()}
          {renderSecondsCountBox()}
          <CustomTextField
            textBoxLabel="Reason"
            placeholder="Eg - Too Many Orders"
            regexCheck={removeSpecialCharacters}
            handleChange={(val: string) => handleInputChange(val, "reason")}
            customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal`}
            parentClass="mt-[24px]"
            value={data?.reason}
            rows={1}
            isMandatory={false}
            maxLength={25}
            errorMessage={errorMsgMap?.reason || ""}
          ></CustomTextField>
        </div>
        {renderActionFooter()}
      </>
    );
  }

  function renderActionFooter() {
    return (
      <div
        className={`absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px]
          px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden
          sm:w-full sm:px-[16px]
        `}
      >
        <CtaComponent
          ctaText={"Discard"}
          ctaType={CTA_TYPE.secondary_cta}
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
          onCtaClick={onModalClose}
          customClass={"mr-10 sm:mr-[8px]"}
        ></CtaComponent>
        <CtaComponent
          ctaText={"Save"}
          ctaType={CTA_TYPE.active_cta}
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
          onCtaClick={onSaveCtaClick}
          customClass={"ml-10 sm:ml-[8px]"}
          isDisabled={!isChangesMade}
        ></CtaComponent>
      </div>
    );
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <div
        className={`outlet-logistic-status-modal absolute w-[636px] rounded-[8px]
        overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white pb-[84px]
        sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
        sm:w-full sm:h-[90%] sm:rounded-b-[0]
      `}
      >
        {renderModalHeader()}
        {logisticStatusApiData?.showMsg ? (
          renderJsonErrorMessage(logisticStatusApiData?.message)
        ) : (
          <>
            {renderDeliveryStatusInfo()}
            {logisticStatusApiData?.data?.self_delivery_serviceability_status
              ? renderLogisticStatusUpdateForm()
              : null}
          </>
        )}
      </div>
    </Modal>
  );
};

export default OutletLogisticStatus;
